import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Dialog, Box, Grid, Typography, Alert } from '@mui/material'
import { ChangeWasteEntryContainerDto, WasteContainerDto, WasteEntryDto, WasteStatus } from '@services/dtos'
import { useAppDispatch } from '@hooks/reduxHooks'
import { changeWasteEntryContainer } from '@state/reducers/wasteEntryReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { wasteContainerService } from '@services/wasteContainerService'
import { WasteTypeLabels } from '@utils/constants'
import { changeWasteEntryContainerSchema } from '@utils/schemas'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import WasteContainerChips from '../WasteContainerChips'
import { FormHeader } from '../forms/FormHeader'

type Props = {
  open: boolean
  entry: WasteEntryDto
  onClose: () => void
}

export function ChangeWasteEntryContainerDialog({ open, entry, onClose }: Props) {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [targetContainer, setTargetContainer] = useState<WasteContainerDto | null>(null)
  const [noCompatibleContainers, setNoCompatibleContainers] = useState<boolean | null>(null)

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ChangeWasteEntryContainerDto>({
    resolver: yupResolver(changeWasteEntryContainerSchema),
  })

  const fetchCompatibleContainers = async (search: string) => {
    const page = await wasteContainerService.getAll({
      search,
      status: WasteStatus.OPEN,
      type: entry.wasteContainer?.type,
      isotopeIds: entry.isotope ? entry.isotope.id : undefined,
      limit: 20,
      page: 0,
    })
    const results = (page?.datas ?? []).filter((c) => c.id !== entry.wasteContainer?.id)
    setNoCompatibleContainers(results.length === 0)
    return results
  }

  const onSubmit = (data: ChangeWasteEntryContainerDto) => {
    setIsSubmitting(true)
    dispatch(changeWasteEntryContainer({ id: entry.id, dto: data }))
      .unwrap()
      .then(() => {
        dispatch(enqueueSnackbar({ message: 'Poubelle modifiée avec succès', options: { variant: 'success' } }))
        onClose()
      })
      .catch(() =>
        dispatch(enqueueSnackbar({ message: 'Une erreur est survenue lors du changement de poubelle', options: { variant: 'error' } })),
      )
      .finally(() => setIsSubmitting(false))
  }

  const container = entry?.wasteContainer

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className="flex w-full h-full p-4">
        <form
          onSubmit={(e) => { e.stopPropagation(); handleSubmit(onSubmit)(e) }}
          className="w-full"
        >
          <FormHeader
            isSubmitting={isSubmitting}
            onClose={onClose}
            title={`Transfert déchet #${entry?.no}`}
            subTitles={container ? [container.label] : []}
            canSubmit={noCompatibleContainers !== true}
          />

          <Grid container rowSpacing={2} mt={1}>
            <Grid item xs={12}>
              <Controller
                name="targetContainerId"
                control={control}
                render={({ field: { onChange } }) => (
                  <>
                    <ServerAutocomplete<WasteContainerDto>
                      autoFetchOnMount
                      disabled={noCompatibleContainers === true}
                      value={targetContainer}
                      fetchService={fetchCompatibleContainers}
                      onChange={(c) => {
                        onChange(c?.id ?? null)
                        setTargetContainer(c ?? null)
                      }}
                      getValue={(c) => c?.id ?? null}
                      getOptionLabel={(value) => {
                        const c = value as WasteContainerDto
                        return c ? c.label : ''
                      }}
                      renderOption={(props, c) => (
                        <li {...props} key={c.id}>
                          <Box sx={{ py: 0.25, width: '100%' }}>
                            <Typography variant="body2" fontWeight={400}>{c.label}</Typography>
                            <WasteContainerChips container={c} />
                          </Box>
                        </li>
                      )}
                      placeholder="Rechercher un conteneur ouvert compatible..."
                      label="Nouveau conteneur"
                      minSearchLength={0}
                      helperText={errors.targetContainerId?.message}
                      error={!!errors.targetContainerId}
                    />
                    {targetContainer && (
                      <Box mt={1}>
                        <WasteContainerChips container={targetContainer} />
                      </Box>
                    )}
                  </>
                )}
              />
              {noCompatibleContainers === true ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Aucun conteneur compatible disponible. Les conteneurs de destination doivent être ouverts, de type {container && WasteTypeLabels[container.type]}
                    {entry?.isotope && ` et accepter l'isotope ${entry.isotope.name} (${entry.isotope.code})`}.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Conteneurs compatibles : type {container && WasteTypeLabels[container.type]}
                    {entry?.isotope && ` · isotope ${entry.isotope.name} (${entry.isotope.code})`}
                  </Typography>
                </Alert>
              )}
            </Grid>
          </Grid>
        </form>
      </div>
    </Dialog>
  )
}
